<template>
  <div class="crew-form">
    <toolbar
      backRoute="/dashboard/crew"
      :hideBackButton="isPopup"
      :beforeBack="checkForChanges"
      :title="isPopup ? '' : toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="crew-item-delete"
      :saveButtonLabel="isPopup ? 'Add new crew' : 'Save crew'"
      saveButtonEvent="crew-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="crew-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="first_name">First name:</label>

                <validation-provider
                  rules="required"
                  name="first_name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.first_name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="last_name">Last name:</label>

                <validation-provider
                  rules="required"
                  name="last_name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.last_name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="birth_date">DOB:</label>
                <d-datepicker
                  v-model="data.birth_date"
                  format="dd / MM / yyyy"
                  placeholder="Select a date" />
              </div>

              <div class="form-group">
                <label for="birth_place">Birth place:</label>
                <d-input v-model="data.birth_place" />
              </div>

              <div class="form-group">
                <label for="uk_address">UK Address:</label>
                <d-input v-model="data.uk_address" />
              </div>

              <div class="form-group">
                <label for="sex">Sex:</label>
                <div>
                  <d-form-radio inline name="sex" v-model="data.sex" value="M">Male</d-form-radio>
                  <d-form-radio inline name="sex" v-model="data.sex" value="F">Female</d-form-radio>
                </div>
              </div>

              <div class="form-group">
                <label for="licences">Licence numbers:</label>

                <licences-list
                  v-if="loadedData"
                  referenceType="CREW"
                  :referenceId="data.id"
                  itemName="crew" />
              </div>

              <div class="form-group">
                <label for="phone">Phone:</label>
                <d-input v-model="data.phone" />
              </div>

              <div class="form-group">
                <label for="email">E-mail:</label>

                <validation-provider
                  rules="email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.email"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="known_as">Known as:</label>
                <d-input v-model="data.known_as" />
              </div>

              <div class="form-group">
                <label>Customer:</label>

                <validation-provider
                  rules="required"
                  name="flight_customer_id"
                  v-slot="{ errors }">
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      :options="operators"
                      v-model="data.flight_customer_id"
                      :reduce="customer => customer.id"
                      :filterBy="filterByCustomers"
                      :maxItemsDisplay="100"
                      :disabled="!!customerID"
                      placeholder="Select a customer"
                      label="id">
                      <template slot="no-options">
                        <template>There are no customers for the provided phrase&hellip;</template>
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                  </div>

                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="address_1">Address line 1:</label>
                <d-input v-model="data.address_1" />
              </div>

              <div class="form-group">
                <label for="addres_2">Address line 2:</label>
                <d-input v-model="data.address_2" />
              </div>

              <div class="form-group">
                <label for="town">Town:</label>
                <d-input v-model="data.town" />
              </div>

              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label for="post_code">Post code:</label>
                    <d-input v-model="data.post_code" />
                  </div>
                </d-col>

                <d-col cols="8">
                  <div class="form-group">
                    <label for="country_id">Country:</label>
                    <div :class="{ 'form-control': true, 'is-select': true }">
                      <v-select
                        :options="countriesOptions"
                        v-model="data.country_id"
                        :reduce="country => country.id"
                        placeholder="Select a country"
                        label="text">
                        <template slot="no-options">
                          There are no countries for the provided phrase&hellip;
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="rewards">Rewards:</label>

                <rewards-list
                    v-if="loadedData"
                    referenceType="CREW"
                    :referenceId="data.id"
                    itemName="crew" />
              </div>

              <div class="form-group">
                <label>
                  Signature:
                </label>

                <div
                  v-if="data.signature_image_url"
                  class="signature">
                  <d-button
                    v-if="!signatureBlobData"
                    :outline="true"
                    class="mb-3"
                    @click.prevent="previewSignature(data.signature_image_url)">
                    View signature
                  </d-button>

                  <img
                    v-if="signatureBlobData"
                    :src="signatureBlobData"
                    alt="" />
                </div>

                <div v-if="data.signature_image_url">
                  <a
                    href="#"
                    @click.prevent="removeSignature()"
                    class="text-danger">
                    <small>Remove signature</small>
                  </a>
                </div>

                <file-input
                  v-if="!data.signature_image_url"
                  :onChange="setSignature"
                  id="inputFileSignature" />
              </div>

              <div class="form-group">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  :rows="10" />
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew"
        itemType="passports" />

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew"
        itemType="documents-licences" />

      <warnings-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew" />

      <visas-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew" />

      <documents-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew"
        itemType="documents" />

      <checklist-list
        v-if="loadedData && !isPopup"
        referenceType="CREW"
        :referenceId="data.id"
        itemName="crew" />
    </d-container>
  </div>
</template>

<script>
import ChecklistList from './../form-parts/CheckLists.vue';
import CommonGetters from './../../utils/CommonGetters.js';
import DateUtils from './../../utils/DateUtils.js';
import DocumentsList from './../form-parts/Documents.vue';
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import LicencesList from './../form-parts/Licences.vue';
import ListUtils from './../../utils/ListUtils.js';
import RewardsList from './../form-parts/Rewards.vue';
import VisasList from './../form-parts/Visas.vue';
import WarningsList from './../form-parts/Warnings.vue';
import DOMPurify from 'dompurify';
import Vue from 'vue';

export default {
  name: 'crew-form',
  mixins: [
    HasChangesDetection
  ],
  props: {
    isPopup: {
      default: false
    },
    customerID: {
      default: null
    }
  },
  components: {
    'checklist-list': ChecklistList,
    'documents-list': DocumentsList,
    'licences-list': LicencesList,
    'rewards-list': RewardsList,
    'visas-list': VisasList,
    'warnings-list': WarningsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCountries && this.loadedOperators;
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new crew';
      }

      return 'Edit crew';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new' || this.isPopup) {
        return false;
      }

      return 'Delete crew';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        first_name: '',
        last_name: '',
        birth_date: '',
        phone: '',
        email: '',
        notes: '',
        known_as: '',
        address_1: '',
        address_2: '',
        town: '',
        post_code: '',
        country_id: null,
        birth_place: '',
        uk_address: '',
        sex: 'M',
        signature_image: '',
        signature_image_url: '',
        flight_customer_id: this.customerID
      },
      operators: [],
      loadedOperators: false,
      countries: [],
      loadError: false,
      loadedData: false,
      loadedCountries: false,
      removeInProgress: false,
      saveInProgress: false,
      previewInProgress: false,
      signatureBlobData: null
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
      this.loadAdditionalData();
    } else {
      this.loadData();
    }

    this.loadAdditionalData();
    this.$bus.$on('crew-item-delete', this.removeCrew);
    this.$bus.$on('crew-item-save', this.validateForm);
  },
  methods: {
    setSignature (event) {
      this.data.signature_image = event.target.files[0];
    },
    removeSignature () {
      this.data.signature_image_url = '';
      this.data.signature_image = '';
    },
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/crews/item/',
        instanceFields: ['id', 'first_name', 'last_name', 'birth_date', 'birth_place', 'uk_address', 'sex', 'phone', 'email', 'notes', 'known_as', 'address_1', 'address_2', 'town', 'post_code', 'country_id', 'flight_customer_id', 'signature_image', 'signature_image_url'],
        loadedKey: 'loadedData',
        successAction: () => {
          this.data = DateUtils.groupConversionStringToDate(this.data, ['birth_date']);
        }
      });

      this.loadAdditionalData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/countries/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedOperators',
        outputKey: 'operators',
        errorKey: 'loadError'
      });
    },
    removeCrew () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/crews/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Crew removed',
        successText: 'The selected crew has been removed',
        confirmTitle: 'Do you really want to remove this crew?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/crew');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['crew-form'], this.checkSimiliarItems);
    },
    checkSimiliarItems () {
      if (this.id > 0) {
        this.submitForm();
        return;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          order: {
            column: 'last_name',
            direction: 'ASC'
          },
          where: {
            last_name: this.data.last_name
          }
        },
        endpoint: '/crews/items?page=1',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during checking data before save. Please try again',
        successAction: (response) => {
          if (!response.data.length) {
            this.submitForm();
            return;
          }

          let listOfItems = response.data.map(person => {
            let output = '<div>';
            output += '<strong>' + person.first_name + ' ' + person.last_name + '</strong>';
            output += ' - ';
            output += person.birth_date;

            if (person.birth_place) {
              output += ' (' + person.birth_place + ')';
            }

            return output + '</div>';
          });

          Vue.swal({
            title: 'Crews with similiar last name exist',
            html: DOMPurify.sanitize('<div class="swal-people-list">' + listOfItems + '</div>'),
            icon: 'question',
            confirmButtonText: 'Save it anyway',
            cancelButtonText: 'Cancel',
            showCancelButton: true,
            buttonsStyling: true
          }).then((result) => {
            if (result.value) {
              this.submitForm();
            }
          });
        }
      });
    },
    submitForm () {
      let imageFields = false;
      let endpointUrl = '/crews/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/crews/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      this.data['birth_date'] = DateUtils.convertDateToString(this.data['birth_date']);

      if (this.data.signature_image) {
        imageFields = ['signature_image'];
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Crew added' : 'Crew saved',
        successText: 'The crew has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: (response) => {
          if (this.isPopup) {
            this.$bus.$emit('add-crew-popup-close');
            this.$bus.$emit('pax-list-crews-reload-needed');

            if (response.item) {
              this.$bus.$emit('show-document-popup', 'add', 'CREW', response.item.id, false, 'passports', true);
            }
          } else {
            this.$router.push('/dashboard/crew');
          }
        }
      }, imageFields);
    },
    filterByCustomers (option, label, search) {
      return option.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    previewSignature (url) {
      this.previewInProgress = false;

      this.$http.post(url, {
        token: window.localStorage.getItem('token')
      }, {
        responseType: 'blob'
      }).then((response) => {
        this.signatureBlobData = URL.createObjectURL(new Blob([response.data]));
        this.previewInProgress = false;
      }).catch(() => {
        this.previewInProgress = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('crew-item-delete', this.removeCrew);
    this.$bus.$off('crew-item-save', this.validateForm);
  }
}
</script>
